<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <div>
      <div
        v-if="!isLoading && !companies.length"
        class="text-center text-muted py-3"
      >
        You aren't associated with any companies yet.
      </div>

      <div v-else>
        <div
          v-for="company in companies"
          :key="company.id"
          class="company-row d-flex align-items-center py-3 px-3"
        >
          <b-avatar
            :src="company.primary_admin_avatar_url || ''"
            :text="initials(company.name)"
            size="40px"
            variant="primary"
            class="mr-3 flex-shrink-0"
          />

          <div class="flex-grow-1">
            <div class="font-weight-bold">{{ company.name }}</div>
            <small v-if="leaveAtFor(company)" class="text-warning d-block">
              Leaving on {{ formatLeaveAt(leaveAtFor(company)) }}
            </small>
            <small v-else-if="joinedAtFor(company)" class="text-muted d-block">
              Joined {{ formatJoinedAt(joinedAtFor(company)) }}
            </small>
          </div>

          <b-button
            v-if="leaveAtFor(company)"
            pill
            size="sm"
            variant="outline-success"
            :disabled="actingCompanyId === company.id"
            @click="onCancelLeave(company)"
          >
            Cancel Leave
          </b-button>
          <b-button
            v-else
            pill
            size="sm"
            variant="outline-danger"
            :disabled="actingCompanyId === company.id"
            @click="onLeave(company)"
          >
            Leave
          </b-button>
        </div>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import { parseServerDate } from '@/utils/dateHelper'

export default {
  data() {
    return {
      isLoading: false,
      companies: [],
      actingCompanyId: null,
    }
  },

  methods: {
    async fetchAssociations() {
      this.isLoading = true
      try {
        const { data } = await this.$api.get('companies/associations')
        this.companies = (data && data.data) || []
      } catch (err) {
        this.toast('Unable to load company associations.', 'danger')
        this.companies = []
      } finally {
        this.isLoading = false
      }
    },

    leaveAtFor(company) {
      return company && company.pivot ? company.pivot.leave_at : null
    },

    joinedAtFor(company) {
      return company && company.pivot ? company.pivot.joined_at : null
    },

    formatLeaveAt(iso) {
      try {
        const d = parseServerDate(iso)
        if (!d) return iso
        return d.toLocaleString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      } catch (e) {
        return iso
      }
    },

    formatJoinedAt(iso) {
      try {
        return new Date(iso).toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      } catch (e) {
        return iso
      }
    },

    initials(name) {
      if (!name) return '?'
      return name
        .split(/\s+/)
        .map((w) => w.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },

    async onLeave(company) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `If you leave "${company.name}", you'll lose access to this company. Are you sure?`,
        {
          title: 'Leave company?',
          okTitle: 'Yes, leave',
          cancelTitle: 'Cancel',
          okVariant: 'danger',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      if (!ok) return

      this.actingCompanyId = company.id
      try {
        const { data } = await this.$api.post('companies/leave', {
          company_id: company.id,
        })
        if (data && data.ok) {
          this.updateLeaveAt(company.id, data.leave_at)
          this.toast(`Leave scheduled for "${company.name}".`, 'success')
        } else {
          this.toast(
            (data && data.message) || 'Unable to schedule leave.',
            'danger',
          )
        }
      } catch (err) {
        const msg =
          (err && err.data && err.data.message) || 'Unable to schedule leave.'
        this.toast(msg, 'danger')
      } finally {
        this.actingCompanyId = null
      }
    },

    async onCancelLeave(company) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Cancel your scheduled leave from "${company.name}"? The company will retain your information.`,
        {
          title: 'Cancel leave?',
          okTitle: 'Yes, cancel leave',
          cancelTitle: 'Keep leave',
          okVariant: 'success',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      if (!ok) return

      this.actingCompanyId = company.id
      try {
        const { data } = await this.$api.post('companies/cancel-leave', {
          company_id: company.id,
        })
        if (data && data.ok) {
          this.updateLeaveAt(company.id, null)
          this.toast(`Leave cancelled for "${company.name}".`, 'success')
        } else {
          this.toast(
            (data && data.message) || 'Unable to cancel leave.',
            'danger',
          )
        }
      } catch (err) {
        const msg =
          (err && err.data && err.data.message) || 'Unable to cancel leave.'
        this.toast(msg, 'danger')
      } finally {
        this.actingCompanyId = null
      }
    },

    updateLeaveAt(companyId, leaveAt) {
      const company = this.companies.find((c) => c.id === companyId)
      if (!company) return
      if (!company.pivot) company.pivot = {}
      this.$set(company.pivot, 'leave_at', leaveAt)
    },

    toast(message, variant = 'primary') {
      this.$bvToast.toast(message, {
        title: variant === 'danger' ? 'Error' : 'Success',
        autoHideDelay: 4000,
        variant,
      })
    },
  },

  mounted() {
    this.fetchAssociations()
    this.$root.$on('refreshCompanyAssociations', this.fetchAssociations)
  },

  beforeDestroy() {
    this.$root.$off('refreshCompanyAssociations', this.fetchAssociations)
  },
}
</script>

<style lang="scss" scoped>
.company-row {
  border-bottom: 1px solid #f1f1f1;
}
.company-row:last-child {
  border-bottom: none;
}
</style>
